import React from 'react';

/** Single vehicle card placeholder — aspect-stable for Discover/Favorites grids */
export const VehicleCardSkeleton: React.FC<{ collectionMode?: boolean }> = () => (
  <div
    className="w-full max-w-[360px] mx-auto aspect-[5/7] rounded-[14px] border border-[#625243] bg-[#101215] p-3 flex flex-col gap-3 overflow-hidden"
    aria-hidden
  >
    <div className="h-4 w-3/4 rounded bg-[#25282d] animate-pulse motion-reduce:animate-none" />
    <div className="flex-1 rounded-lg bg-[#1b1e23] animate-pulse motion-reduce:animate-none" />
    <div className="h-16 rounded-lg bg-[#1b1e23] animate-pulse motion-reduce:animate-none" />
    <div className="h-11 rounded-lg bg-[#25282d] animate-pulse motion-reduce:animate-none" />
  </div>
);

interface DiscoverSkeletonProps {
  /** Full page chrome + grid (initial load) */
  fullPage?: boolean;
  /** Card count hint for mobile-first */
  cardCount?: number;
}

export const DiscoverSkeleton: React.FC<DiscoverSkeletonProps> = ({
  fullPage = true,
  cardCount = 6,
}) => (
  <div role="status" aria-busy="true" aria-live="polite">
    <span className="sr-only">Loading builds…</span>
    {fullPage ? (
      <>
        <div className="h-10 w-48 rounded bg-[#141414] mb-3 animate-pulse motion-reduce:animate-none" />
        <div className="h-4 w-full max-w-xl rounded bg-[#141414]/60 mb-8 animate-pulse motion-reduce:animate-none" />
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 h-12 rounded-lg bg-[#0a0a0a] border border-white/[0.06] animate-pulse motion-reduce:animate-none" />
          <div className="h-12 w-24 rounded-lg bg-[#141414] animate-pulse motion-reduce:animate-none" />
          <div className="h-12 w-24 rounded-lg bg-[#141414] animate-pulse motion-reduce:animate-none" />
        </div>
        <div className="flex gap-2 mb-6">
          <div className="h-9 w-28 rounded-full bg-[#141414]/80 animate-pulse motion-reduce:animate-none" />
          <div className="h-9 w-36 rounded-lg bg-[#141414]/60 animate-pulse motion-reduce:animate-none ml-auto" />
        </div>
      </>
    ) : null}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {Array.from({ length: cardCount }).map((_, i) => (
        <VehicleCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

interface FavoritesSkeletonProps {
  fullPage?: boolean;
  cardCount?: number;
  /** Compact rail for homepage module */
  compact?: boolean;
}

export const FavoritesSkeleton: React.FC<FavoritesSkeletonProps> = ({
  fullPage = true,
  cardCount = 6,
  compact = false,
}) => {
  if (compact) {
    return (
      <div role="status" aria-busy="true" className="space-y-3">
        <span className="sr-only">Loading favorite builds…</span>
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-36 space-y-2" aria-hidden>
              <div className="aspect-[4/3] rounded-lg bg-[#141414] animate-pulse motion-reduce:animate-none" />
              <div className="h-3 w-4/5 rounded bg-[#141414]/70 animate-pulse motion-reduce:animate-none" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading favorites…</span>
      {fullPage ? (
        <>
          <div className="h-10 w-56 rounded bg-[#141414] mb-3 animate-pulse motion-reduce:animate-none" />
          <div className="h-4 w-72 rounded bg-[#141414]/60 mb-8 animate-pulse motion-reduce:animate-none" />
        </>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {Array.from({ length: cardCount }).map((_, i) => (
          <VehicleCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
};

export default DiscoverSkeleton;
